import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Text } from "@otrium/atoms";
import { useContext, useEffect } from "react";
import { useSortBy } from "react-instantsearch-core-v7";
import { DropdownInline } from "src/atoms/DropDown";
import { AppCtx } from "src/contexts/app.context";
import { theme } from "src/theme";
import { handleSearchParams } from "src/utils/handle-search-params";

const orderByKeyMap: Record<string, string> = {
  publish_date_desc: "new",
  discount_date_desc: "new-discount",
  price_desc: "price-desc",
  price_asc: "price-asc",
  popular: "popular",
} as const;

const orderByValueMap: Record<string, string> = {
  new: "publish_date_desc",
  "new-discount": "discount_date_desc",
  "price-desc": "price_desc",
  "price-asc": "price_asc",
  popular: "",
} as const;

function Sort(): JSX.Element {
  const { locale } = useContext(AppCtx);
  const { i18n } = useLingui();
  const defaultIndexName = `${locale}_products`;

  const { refine, options, currentRefinement } = useSortBy({
    items: [
      {
        label: t(i18n)`Popularity`,
        value: defaultIndexName,
      },
      {
        label: t(i18n)`New`,
        value: `${defaultIndexName}_publish_date_desc`,
      },
      {
        label: t(i18n)`New discount`,
        value: `${defaultIndexName}_discount_date_desc`,
      },
      {
        label: t(i18n)`Price: high to low`,
        value: `${defaultIndexName}_price_desc`,
      },
      {
        label: t(i18n)`Price: low to high`,
        value: `${defaultIndexName}_price_asc`,
      },
    ],
  });

  useEffect(() => {
    const url = new URL(location.toString());
    const orderByDefaultValue = url.searchParams.get("orderBy");

    if (!orderByDefaultValue) {
      refine(defaultIndexName);
      return;
    }

    if (orderByDefaultValue === "popular") {
      return;
    }

    refine(`${defaultIndexName}_${orderByValueMap[orderByDefaultValue]}`);
  }, [defaultIndexName, refine]);

  return (
    <>
      <Text fontSize={theme.fontSizes.fontSize14}>
        <Trans>sort by</Trans>
      </Text>
      <DropdownInline
        data-testid="product-sorting-select"
        value={currentRefinement || defaultIndexName}
        options={options.map(({ label, value }) => ({ text: label, value }))}
        onChange={(_event: MouseEvent, { value }: { value: string }) => {
          const valueKey = value.replace(`${defaultIndexName}_`, "");

          handleSearchParams({
            facet: "orderBy",
            value:
              orderByKeyMap[
                valueKey.length > 0 && value !== defaultIndexName
                  ? valueKey
                  : "popular"
              ],
            isSingle: true,
          });
          refine(value);
        }}
      />
    </>
  );
}

export { Sort };
